// ============================================
// VOLUNTEER MODULE - EVENTS PAGE
// Wizard: Event → Venue (with its own gates)
// Cards clickable to view event detail
// ============================================

import React, { useState, useEffect } from "react";
import { useTheme } from "../components/ThemeContext";
import {
  getEvents, createEvent, deleteEvent,
  createVenue, createGate,
} from "../services/api";

const EVENT_TYPES = ["Conference","Festival","Sports","Religious","Music","Exhibition","Other"];
const STATUS_OPTS = ["Draft","Active","Completed","Cancelled"];
const TIMEZONES   = ["UTC","Asia/Dubai","Asia/Riyadh","Asia/Kuwait","Africa/Lagos","Europe/London","America/New_York","Asia/Karachi","Asia/Kolkata","Asia/Singapore"];

const sColor = (s,C) => ({ Active:C.green, Draft:C.orange, Completed:C.accent, Cancelled:C.red }[s]||C.muted);
const tColor = (t,C) => ({ Conference:C.accent, Festival:C.orange, Sports:C.green, Religious:C.purple, Music:C.red, Exhibition:C.purple, Other:C.muted }[t]||C.muted);

const Badge = ({ text, color }) => (
  <span className="vh-badge" style={{ background:`${color}22`, color, border:`1px solid ${color}44` }}>{text}</span>
);

const Msg = ({ msg, C }) => msg ? (
  <div style={{ padding:"11px 16px", borderRadius:10, marginBottom:14, background:msg.type==="success"?C.greenSoft:C.redSoft, border:`1px solid ${msg.type==="success"?C.green:C.red}44`, color:msg.type==="success"?C.green:C.red, fontSize:13, fontWeight:600 }}>
    {msg.text}
  </div>
) : null;

// ── WIZARD PROGRESS ─────────────────────────
function WizardProgress({ steps, current, C }) {
  return (
    <div className="wizard-steps" style={{ marginBottom:28 }}>
      {steps.map((step,i) => {
        const done   = current > i+1;
        const active = current === i+1;
        return (
          <div key={i} className="wizard-step">
            <div className="wizard-step-circle"
              style={{ background:done?C.green:active?C.accent:C.border, color:done||active?"#000":C.muted }}>
              {done?"✓":i+1}
            </div>
            <span className="wizard-step-label" style={{ color:active?C.text:done?C.green:C.muted }}>{step}</span>
            {i < steps.length-1 && <div className="wizard-step-line" style={{ background:done?C.green:C.border }} />}
          </div>
        );
      })}
    </div>
  );
}

// ── EQUIPMENT MANAGER ───────────────────────
function EquipmentManager({ equipment, onChange, C }) {
  const add = () => onChange([...equipment, { id:Date.now(), name:"", quantity:"1" }]);
  const upd = (id,k,v) => onChange(equipment.map(e=>e.id===id?{...e,[k]:v}:e));
  const rem = (id) => onChange(equipment.filter(e=>e.id!==id));

  return (
    <div style={{ marginTop:4 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:C.text }}>🛠️ Equipment Required</div>
          <div style={{ fontSize:11, color:C.muted }}>Record only — no inventory tracking</div>
        </div>
        <button onClick={add} style={{ padding:"6px 12px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7, color:C.accent, fontSize:11, fontWeight:700, cursor:"pointer" }}>+ Add</button>
      </div>
      {equipment.length===0 ? (
        <div style={{ padding:12, background:C.bg, border:`1px dashed ${C.border}`, borderRadius:9, textAlign:"center", fontSize:12, color:C.muted }}>Click "+ Add" to list equipment needed at this gate.</div>
      ) : equipment.map(item => (
        <div key={item.id} style={{ display:"grid", gridTemplateColumns:"1fr 80px 34px", gap:6, marginBottom:6, alignItems:"center" }}>
          <input value={item.name} onChange={e=>upd(item.id,"name",e.target.value)} placeholder="e.g. Laptop, Scanner..."
            style={{ padding:"8px 11px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, fontSize:12, outline:"none" }} />
          <input type="number" value={item.quantity} onChange={e=>upd(item.id,"quantity",e.target.value)} min="1"
            style={{ padding:"8px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, fontSize:12, outline:"none", textAlign:"center" }} />
          <button onClick={()=>rem(item.id)} style={{ padding:"8px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:7, color:C.red, fontSize:11, fontWeight:700, cursor:"pointer" }}>✕</button>
        </div>
      ))}
    </div>
  );
}

// ── GATE MINI FORM (per venue) ──────────────
function GateMiniForm({ onAdd, C }) {
  const [gate, setGate]       = useState({ name:"", gateType:"Entry", minVolunteers:"", maxVolunteers:"", notes:"" });
  const [equipment, setEquip] = useState([]);
  const [saving, setSaving]   = useState(false);

  const sg = (k,v) => setGate(f=>({...f,[k]:v}));
  const inp = { width:"100%", padding:"9px 12px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, fontSize:13, outline:"none", boxSizing:"border-box" };

  const handleAdd = async () => {
    if(!gate.name) return;
    setSaving(true);
    await onAdd({ ...gate, equipment });
    setGate({ name:"", gateType:"Entry", minVolunteers:"", maxVolunteers:"", notes:"" });
    setEquip([]);
    setSaving(false);
  };

  return (
    <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:12, padding:16, marginTop:12 }}>
      <div style={{ fontSize:12, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:"0.4px", marginBottom:12 }}>Add Gate to This Venue</div>
      <div className="form-grid-2">
        <div>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Gate Name <span style={{color:C.red}}>*</span></div>
          <input value={gate.name} onChange={e=>sg("name",e.target.value)} placeholder="e.g. Gate A, VIP Entry" style={inp} />
        </div>
        <div>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Gate Type</div>
          <select value={gate.gateType} onChange={e=>sg("gateType",e.target.value)} style={{ ...inp, cursor:"pointer" }}>
            {["Entry","Exit","VIP","Parking","Emergency","Media"].map(o=><option key={o}>{o}</option>)}
          </select>
        </div>
        <div style={{ padding:12, background:C.greenSoft, border:`1px solid ${C.green}44`, borderRadius:10 }}>
          <div style={{ fontSize:10, color:C.green, marginBottom:5, fontWeight:700, textTransform:"uppercase" }}>Min Volunteers</div>
          <input type="number" value={gate.minVolunteers} onChange={e=>sg("minVolunteers",e.target.value)} placeholder="e.g. 2" min="0"
            style={{ ...inp, border:`1px solid ${C.green}66`, fontSize:16, fontWeight:800, textAlign:"center" }} />
        </div>
        <div style={{ padding:12, background:C.orangeSoft, border:`1px solid ${C.orange}44`, borderRadius:10 }}>
          <div style={{ fontSize:10, color:C.orange, marginBottom:5, fontWeight:700, textTransform:"uppercase" }}>Max Volunteers</div>
          <input type="number" value={gate.maxVolunteers} onChange={e=>sg("maxVolunteers",e.target.value)} placeholder="e.g. 5" min="0"
            style={{ ...inp, border:`1px solid ${C.orange}66`, fontSize:16, fontWeight:800, textAlign:"center" }} />
        </div>
        <div style={{ gridColumn:"1 / -1" }}>
          <EquipmentManager equipment={equipment} onChange={setEquip} C={C} />
        </div>
      </div>
      <div style={{ marginTop:12, display:"flex", justifyContent:"flex-end" }}>
        <button onClick={handleAdd} disabled={saving||!gate.name}
          style={{ padding:"8px 20px", background:gate.name?C.accentSoft:C.border, border:`1px solid ${gate.name?C.accent+"44":"transparent"}`, borderRadius:9, color:gate.name?C.accent:C.muted, fontSize:12, fontWeight:700, cursor:gate.name?"pointer":"not-allowed", opacity:gate.name?1:0.6, transition:"all 0.2s" }}>
          {saving?"Saving...":"+ Save Gate"}
        </button>
      </div>
    </div>
  );
}

// ── VENUE BLOCK (in wizard step 2) ─────────
function VenueBlock({ venue, venueIdx, onAddGate, C }) {
  const [showGateForm, setShowGateForm] = useState(false);

  const handleAddGate = async (gateData) => {
    await onAddGate(venueIdx, gateData);
    setShowGateForm(false);
  };

  return (
    <div style={{ background:C.card, border:`1px solid ${C.green}44`, borderRadius:13, padding:16, marginBottom:12 }}>
      {/* Venue Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:28, height:28, borderRadius:"50%", background:C.greenSoft, border:`1px solid ${C.green}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color:C.green, flexShrink:0 }}>
            {venueIdx+1}
          </div>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:C.green }}>✅ {venue.name}</div>
            <div style={{ fontSize:11, color:C.muted }}>📍 {venue.address}{venue.capacity?` · 👥 ${venue.capacity}`:""}</div>
          </div>
        </div>
        <button onClick={() => setShowGateForm(s=>!s)}
          style={{ padding:"6px 14px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:8, color:C.accent, fontSize:12, fontWeight:700, cursor:"pointer" }}>
          🚪 {showGateForm?"Hide":"Add Gate"}
        </button>
      </div>

      {/* Gates already added to this venue */}
      {(venue.gates||[]).length > 0 && (
        <div style={{ marginLeft:38, marginBottom:8 }}>
          <div style={{ fontSize:10, color:C.muted, fontWeight:700, textTransform:"uppercase", marginBottom:5 }}>Gates Added</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {(venue.gates||[]).map((g,i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 10px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7 }}>
                <span style={{ fontSize:12, color:C.accent, fontWeight:700 }}>🚪 {g.name}</span>
                <span style={{ fontSize:10, color:C.muted }}>· Min:{g.minVolunteers||0} Max:{g.maxVolunteers||0}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Gate form */}
      {showGateForm && <GateMiniForm onAdd={handleAddGate} C={C} />}
    </div>
  );
}

// ── EVENT WIZARD ────────────────────────────
function EventWizard({ onDone, onViewEvent, C }) {
  const [step, setStep]     = useState(1);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState(null);

  // Step 1 data
  const [eventData, setEventData] = useState({ name:"", description:"", eventType:"", status:"Draft", startDate:"", endDate:"", startTime:"", endTime:"", timezone:"UTC" });
  const [eventId, setEventId]     = useState(null);
  const [createdEvent, setCreatedEvent] = useState(null);

  // Step 2 data — each venue has its own gates array
  const [venues, setVenues]   = useState([]);
  const [newVenue, setNewVenue] = useState({ name:"", address:"", capacity:"", lat:"", lng:"", mapUrl:"", notes:"" });

  const STEPS = ["Event Details","Venues & Gates","Done"];
  const se = (k,v) => setEventData(f=>({...f,[k]:v}));
  const sv = (k,v) => setNewVenue(f=>({...f,[k]:v}));

  const inp = { width:"100%", padding:"10px 13px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:9, color:C.text, fontSize:13, outline:"none", boxSizing:"border-box" };
  const sel = { ...inp, cursor:"pointer" };

  // Required fields check
  const canCreate = eventData.name && eventData.eventType && eventData.startDate;
  const canAddVenue = newVenue.name && newVenue.address;

  // ── STEP 1: Create Event ─────────────────
  const handleCreateEvent = async () => {
    if(!canCreate) return;
    setSaving(true); setMsg(null);
    try {
      const r = await createEvent(eventData);
      const id = r.data.event?.id || `EVT_${Date.now()}`;
      setEventId(id);
      setCreatedEvent({ ...eventData, id });
      setMsg({ type:"success", text:`✅ Event "${eventData.name}" created! Now add venues.` });
      setTimeout(() => { setMsg(null); setStep(2); }, 800);
    } catch { setMsg({ type:"error", text:"Failed to create event. Is the backend running?" }); }
    setSaving(false);
  };

  // ── STEP 2: Add Venue ────────────────────
  const handleAddVenue = async () => {
    if(!canAddVenue) return;
    setSaving(true); setMsg(null);
    try {
      const r = await createVenue({ ...newVenue, eventId });
      const venueId = r.data.venue?.id || `VEN_${Date.now()}`;
      setVenues(v => [...v, { ...newVenue, id:venueId, gates:[] }]);
      setNewVenue({ name:"", address:"", capacity:"", lat:"", lng:"", mapUrl:"", notes:"" });
      setMsg({ type:"success", text:`✅ Venue "${newVenue.name}" added! Add its gates below.` });
      setTimeout(() => setMsg(null), 1500);
    } catch { setMsg({ type:"error", text:"Failed to add venue." }); }
    setSaving(false);
  };

  // ── Add Gate to a specific venue ─────────
  const handleAddGate = async (venueIdx, gateData) => {
    const venue = venues[venueIdx];
    try {
      await createGate({ ...gateData, venueId:venue.id, venueName:venue.name, eventId });
      // Update local venues state with the new gate
      setVenues(v => v.map((ven, i) =>
        i === venueIdx ? { ...ven, gates:[...(ven.gates||[]), gateData] } : ven
      ));
    } catch { setMsg({ type:"error", text:"Failed to add gate." }); }
  };

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:22, fontWeight:800, color:C.text, fontFamily:"Georgia, serif" }}>Create New Event</div>
        <div style={{ fontSize:13, color:C.muted, marginTop:4 }}>Set up your Event → Venue → Gate structure</div>
      </div>

      <WizardProgress steps={STEPS} current={step} C={C} />
      <Msg msg={msg} C={C} />

      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:"28px 32px" }}>

        {/* ── STEP 1 ── */}
        {step===1 && (
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:6 }}>📅 Event Details</div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:16 }}>
              Fields marked <span style={{color:C.red}}>*</span> are required.
              {!canCreate && (
                <span style={{ marginLeft:8, color:C.orange, fontWeight:600 }}>
                  ⚠️ Still needed: {[!eventData.name&&"Event Name",!eventData.eventType&&"Event Type",!eventData.startDate&&"Start Date"].filter(Boolean).join(", ")}
                </span>
              )}
            </div>
            <div className="form-grid-2">
              <div style={{ gridColumn:"1 / -1" }}>
                <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Event Name <span style={{color:C.red}}>*</span></div>
                <input value={eventData.name} onChange={e=>se("name",e.target.value)} placeholder="e.g. Music Fair 2026, World Youth Forum" style={inp} />
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Event Type <span style={{color:C.red}}>*</span></div>
                <select value={eventData.eventType} onChange={e=>se("eventType",e.target.value)} style={sel}>
                  <option value="">Select type...</option>
                  {EVENT_TYPES.map(o=><option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Status</div>
                <select value={eventData.status} onChange={e=>se("status",e.target.value)} style={sel}>
                  {STATUS_OPTS.map(o=><option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Start Date <span style={{color:C.red}}>*</span></div>
                <input type="date" value={eventData.startDate} onChange={e=>se("startDate",e.target.value)} style={inp} />
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>End Date</div>
                <input type="date" value={eventData.endDate} onChange={e=>se("endDate",e.target.value)} style={inp} />
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Start Time</div>
                <input type="time" value={eventData.startTime} onChange={e=>se("startTime",e.target.value)} style={inp} />
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>End Time</div>
                <input type="time" value={eventData.endTime} onChange={e=>se("endTime",e.target.value)} style={inp} />
              </div>
              <div style={{ gridColumn:"1 / -1" }}>
                <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Timezone</div>
                <select value={eventData.timezone} onChange={e=>se("timezone",e.target.value)} style={sel}>
                  {TIMEZONES.map(o=><option key={o}>{o}</option>)}
                </select>
              </div>
              <div style={{ gridColumn:"1 / -1" }}>
                <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Description</div>
                <textarea value={eventData.description} onChange={e=>se("description",e.target.value)} placeholder="Brief description..." rows={2}
                  style={{ ...inp, resize:"vertical" }} />
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2 ── */}
        {step===2 && (
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:4 }}>🏟️ Add Venues & Gates</div>
            <div style={{ fontSize:13, color:C.muted, marginBottom:20 }}>
              Each venue has its own gates. Add a venue then immediately add gates for it.
            </div>

            {/* Existing venues with their gates */}
            {venues.map((venue, idx) => (
              <VenueBlock key={idx} venue={venue} venueIdx={idx} onAddGate={handleAddGate} C={C} />
            ))}

            {/* New venue form */}
            <div style={{ background:C.bg, border:`1px dashed ${C.border}`, borderRadius:13, padding:20, marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:14 }}>
                {venues.length===0 ? "Add your first venue" : "Add another venue"}
              </div>
              <div className="form-grid-2">
                <div>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Venue Name <span style={{color:C.red}}>*</span></div>
                  <input value={newVenue.name} onChange={e=>sv("name",e.target.value)} placeholder="e.g. Main Stage, Hall A" style={inp} />
                </div>
                <div>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Capacity</div>
                  <input value={newVenue.capacity} onChange={e=>sv("capacity",e.target.value)} placeholder="e.g. 5000" style={inp} />
                </div>
                <div style={{ gridColumn:"1 / -1" }}>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Address <span style={{color:C.red}}>*</span></div>
                  <input value={newVenue.address} onChange={e=>sv("address",e.target.value)} placeholder="Full address or location" style={inp} />
                </div>
                <div>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Latitude</div>
                  <input value={newVenue.lat} onChange={e=>sv("lat",e.target.value)} placeholder="e.g. 25.2285" style={inp} />
                </div>
                <div>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Longitude</div>
                  <input value={newVenue.lng} onChange={e=>sv("lng",e.target.value)} placeholder="e.g. 55.2867" style={inp} />
                </div>
                <div style={{ gridColumn:"1 / -1" }}>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.4px" }}>Map URL</div>
                  <input value={newVenue.mapUrl} onChange={e=>sv("mapUrl",e.target.value)} placeholder="https://maps.google.com/..." style={inp} />
                </div>
              </div>
              <div style={{ marginTop:14, display:"flex", gap:10, justifyContent:"flex-end" }}>
                <button onClick={handleAddVenue} disabled={saving||!canAddVenue}
                  style={{ padding:"9px 20px", background:canAddVenue?C.accentSoft:C.border, border:`1px solid ${canAddVenue?C.accent+"44":"transparent"}`, borderRadius:10, color:canAddVenue?C.accent:C.muted, fontSize:13, fontWeight:700, cursor:canAddVenue?"pointer":"not-allowed", opacity:canAddVenue?1:0.6, transition:"all 0.2s" }}>
                  {saving?"Saving...":"+ Save Venue"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: DONE ── */}
        {step===3 && (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:52, marginBottom:14 }}>🎉</div>
            <div style={{ fontSize:20, fontWeight:800, color:C.text, marginBottom:8 }}>Event Setup Complete!</div>
            <div style={{ fontSize:14, color:C.muted, marginBottom:24 }}>
              <strong style={{color:C.accent}}>{eventData.name}</strong> · {venues.length} venue(s) · {venues.reduce((acc,v)=>acc+(v.gates||[]).length,0)} gate(s) total
            </div>
            <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
              {venues.map((v,i) => (
                <div key={i} style={{ padding:"12px 16px", background:C.card, border:`1px solid ${C.border}`, borderRadius:12, textAlign:"left", minWidth:140 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>🏟️ {v.name}</div>
                  {(v.gates||[]).map((g,j) => (
                    <div key={j} style={{ fontSize:11, color:C.muted, marginBottom:2 }}>🚪 {g.name} · Min:{g.minVolunteers||0} Max:{g.maxVolunteers||0}</div>
                  ))}
                  {(v.gates||[]).length===0 && <div style={{ fontSize:11, color:C.muted }}>No gates added</div>}
                </div>
              ))}
            </div>
            <div style={{ display:"flex", gap:10, justifyContent:"center", marginTop:24, flexWrap:"wrap" }}>
              <button onClick={() => onViewEvent && createdEvent ? onViewEvent(createdEvent) : onDone()}
                style={{ padding:"11px 24px", background:C.accent, border:"none", borderRadius:11, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                View Event Details →
              </button>
              <button onClick={onDone}
                style={{ padding:"11px 24px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:11, color:C.accent, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                Back to All Events
              </button>
            </div>
          </div>
        )}

        {/* ── WIZARD NAV ── */}
        {step < 3 && (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:28, paddingTop:20, borderTop:`1px solid ${C.border}`, flexWrap:"wrap", gap:10 }}>
            <button onClick={() => { if(step===1) onDone(); else setStep(s=>s-1); }}
              style={{ padding:"10px 20px", background:"none", border:`1px solid ${C.border}`, borderRadius:10, color:C.muted, fontSize:13, fontWeight:600, cursor:"pointer" }}>
              ← {step===1?"Cancel":"Back"}
            </button>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              {step===1 && (
                <button onClick={handleCreateEvent} disabled={saving||!canCreate}
                  style={{ padding:"10px 24px", background:canCreate?C.accent:C.border, border:"none", borderRadius:10, color:canCreate?"#000":C.muted, fontSize:13, fontWeight:700, cursor:canCreate?"pointer":"not-allowed", opacity:canCreate?1:0.6, transition:"all 0.2s" }}>
                  {saving?"Creating...":"Create Event →"}
                </button>
              )}
              {step===2 && (
                <button onClick={() => setStep(3)}
                  style={{ padding:"10px 20px", background:C.greenSoft, border:`1px solid ${C.green}44`, borderRadius:10, color:C.green, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                  {venues.length>0?"Finish Setup →":"Skip & Finish →"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── EVENTS LIST ─────────────────────────────
function EventsList({ onCreateNew, onViewEvent, C }) {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState("");

  const loadEvents = async () => {
    setLoading(true);
    try { const r = await getEvents(); setEvents(r.data.events||[]); }
    catch { setEvents([]); }
    setLoading(false);
  };

  useEffect(() => { loadEvents(); }, []);

  const handleDelete = async (e, id, name) => {
    e.stopPropagation();
    if(!window.confirm(`Delete event "${name}"?`)) return;
    try {
      const { deleteEvent } = await import("../services/api");
      await deleteEvent(id);
      loadEvents();
    } catch(err) {
      const msg = err.response?.data?.message || "Failed to delete.";
      alert(msg);
    }
  };

  const filtered = events.filter(ev =>
    !search || ev.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Header */}
      <div className="flex-between page-header" style={{ marginBottom:20 }}>
        <div>
          <div style={{ fontSize:22, fontWeight:800, color:C.text, fontFamily:"Georgia, serif" }}>Events</div>
          <div style={{ fontSize:13, color:C.muted, marginTop:4 }}>
            {events.length} event(s) · Click any event card to view details
          </div>
        </div>
        <button onClick={onCreateNew}
          style={{ padding:"10px 20px", background:C.accent, border:"none", borderRadius:10, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
          + Create Event
        </button>
      </div>

      {/* Search */}
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍  Search events..."
        style={{ width:"100%", padding:"10px 14px", background:C.card, border:`1px solid ${C.border}`, borderRadius:10, color:C.text, fontSize:13, outline:"none", boxSizing:"border-box", marginBottom:16 }} />

      {/* List */}
      {loading ? (
        <div style={{ padding:40, textAlign:"center", color:C.muted }}>⏳ Loading events...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding:60, textAlign:"center", background:C.card, border:`1px solid ${C.border}`, borderRadius:16 }}>
          <div style={{ fontSize:48, marginBottom:14 }}>📅</div>
          <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:8 }}>No events yet</div>
          <div style={{ fontSize:13, color:C.muted, marginBottom:20 }}>Create your first event to get started</div>
          <button onClick={onCreateNew}
            style={{ padding:"10px 24px", background:C.accent, border:"none", borderRadius:10, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>
            + Create Your First Event
          </button>
        </div>
      ) : filtered.map((ev, i) => (
        <div key={i}
          onClick={() => onViewEvent(ev)}
          style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, padding:"18px 20px", marginBottom:12, cursor:"pointer", transition:"all 0.2s" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor=C.accent; e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow=C.shadow; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor=C.border; e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="none"; }}>
          <div className="flex-between" style={{ flexWrap:"wrap", gap:12 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
                <span style={{ fontSize:15, fontWeight:800, color:C.text }}>{ev.name}</span>
                <Badge text={ev.status} color={sColor(ev.status,C)} />
                {ev.eventType && <Badge text={ev.eventType} color={tColor(ev.eventType,C)} />}
              </div>
              {ev.description && <div style={{ fontSize:12, color:C.muted, marginBottom:4 }}>{ev.description}</div>}
              <div style={{ display:"flex", gap:14, fontSize:12, color:C.muted, flexWrap:"wrap" }}>
                {ev.startDate && <span>📅 {ev.startDate}{ev.endDate?` → ${ev.endDate}`:""}</span>}
                {ev.startTime && <span>⏰ {ev.startTime}{ev.endTime?` – ${ev.endTime}`:""}</span>}
              </div>
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
              <span style={{ fontSize:12, color:C.accent, fontWeight:600 }}>View Details →</span>
              {(() => {
                const hasVenues = (ev.venueCount||0) > 0 || (ev.gateCount||0) > 0;
                const tip = hasVenues
                  ? `Cannot delete: ${ev.venueCount||0} venue${(ev.venueCount||0)!==1?"s":""} and ${ev.gateCount||0} gate${(ev.gateCount||0)!==1?"s":""} must be deleted first`
                  : "Delete this event";
                return (
                  <button
                    onClick={e => { e.stopPropagation(); if(!hasVenues) handleDelete(e, ev.id, ev.name); }}
                    disabled={hasVenues}
                    title={tip}
                    style={{
                      padding:"6px 12px", borderRadius:8, fontSize:12, fontWeight:700,
                      background: hasVenues ? C.border  : C.redSoft,
                      border:    `1px solid ${hasVenues ? C.border : C.red+"33"}`,
                      color:     hasVenues ? C.muted   : C.red,
                      cursor:    hasVenues ? "not-allowed" : "pointer",
                      opacity:   hasVenues ? 0.5 : 1,
                    }}>
                    🗑️
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── MAIN EVENTS PAGE ────────────────────────
export default function Events({ onViewEvent }) {
  const { C } = useTheme();
  const [view, setView] = useState("list");

  return view === "list"
    ? <EventsList onCreateNew={() => setView("create")} onViewEvent={onViewEvent} C={C} />
    : <EventWizard onDone={() => setView("list")} onViewEvent={onViewEvent} C={C} />;
}